import type { AddTypeForViewModelType } from "@/components/features/_model";
import type { ForNodeType } from "@/components/features/_model/types";

/**
 * @description 階層を問わない ViewModel へ変換する際に拡張しないプロパティ名
 *
 * @see {ExcludePropUnionType}
 */
const BaseExcludeKeys: Array<string> = ["id", "__typename"];

/* eslint-disable @typescript-eslint/no-namespace */
export namespace ViewModelPathUtil {
	/**
	 * @description InputModel の path を受け取って、ViewModel の path に変換する
	 *
	 * @example
	 * ```
	 * [ 'hotelInfo', 0, 'title' ] => [ 'hotelInfo', 'value', 0, 'title' ]
	 * ```
	 */
	export const pathConverter: (
		_path: Array<string | number>,
		_excludeKeys: Array<string>,
	) => Array<string | number> = (_path, _excludeKeys) => {
		/**
		 * 1階層目の除外対象の key に合致するか
		 */
		const hasMatchedRootExcludedKey = _excludeKeys.includes(_path[0] as string);
		/**
		 * 階層を問わない除外対象 key に合致するか
		 */
		const hasMatchedExcludedKey = _path.some((key) => BaseExcludeKeys.includes(key as string));
		/**
		 * 除外対象であればから配列を返す
		 */
		if (hasMatchedRootExcludedKey || hasMatchedExcludedKey) return [];

		const convertedPath = _path
			.map((key, index) => {
				if (typeof key === "string") return _path.length === index ? [key] : [key, "value"];
				if (typeof key === "number") return [key];
				return [];
			})
			.flat();
		// 末尾が key = 'value' であれば削除
		if (convertedPath[convertedPath.length - 1] === "value") {
			convertedPath.pop();
		}
		return convertedPath;
	};

	/**
	 * @description viewModel と path を受け取って、path で指定された viewModel の object を返す
	 *
	 * @example
	 * ```
	 * In.
	 * path = [ 'hotelInfo', 0, 'title' ]
	 * viewModel = {
	 * 	hotelInfo: {
	 * 		value: [
	 * 			{
	 * 		    title: {
	 * 			    value: "title1",
	 *          isEdited: false,
	 * 		    },
	 * 		    description: {
	 * 			    value: "description1",
	 * 				  isEdited: false,
	 * 		    },
	 * 	    },
	 * 	    {
	 * 		    title: {
	 * 			    value: "title2",
	 * 			    isEdited: false,
	 * 		    },
	 * 		    description: {
	 * 			    value: "description2",
	 * 			    isEdited: false,
	 * 		    },
	 * 	    },
	 * 		],
	 * 	},
	 * }
	 *
	 * Out.
	 * {
	 * 	value: "title1",
	 * 	isEdited: false,
	 * }
	 * ```
	 */
	export const getObjectWithViewModelPath: (
		_path: Array<string | number>,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		_viewModel: { [key: string | number]: any },
	) => AddTypeForViewModelType | undefined = (_path, _viewModel) => {
		try {
			// ViewModel がジェネリクスで型が定義されているので、返却値は as で型を指定する
			return _path.reduce((acc, key) => {
				// key === 'value' && acc !== Array の場合は、ViewModel の拡張なので key をしていせずに後続の Node を返す
				if (key === "value" && !acc[key]) return acc;

				/**
				 * @caution デグレが発生する可能性がある
				 *
				 * @description 値が存在しない key にアクセスした場合は、初期値を代入して初期化している
				 *
				 * 型としては必須だが入力時のUX向上の観点で、一時的にnull, undefined が存在する場合を想定している
				 *
				 */
				if (!acc[key]) {
					acc[key] = {
						value: undefined,
						error: undefined,
						isEdited: false,
					} satisfies ForNodeType<undefined>;
				}

				return acc[key];
			}, _viewModel) as AddTypeForViewModelType;
		} catch {
			// 例外を undefined として丸める
			return undefined;
		}
	};

	/**
	 *
	 * @description path の親の path を返す
	 *
	 * @example
	 * ```
	 * In.
	 * [ 'hotelPolicy', 'value', 'hotelInfo', 'value', 0, 'title' ]
	 * Out.
	 * [ ['hotelPolicy'], [ 'hotelPolicy', 'value'], ['hotelPolicy', 'value', 'hotelInfo'], ['hotelPolicy', 'value', 'hotelInfo', 'value'], ['hotelPolicy', 'value', 'hotelInfo', 'value', 0] ]
	 * ```
	 *
	 */
	export const getViewModelParentPaths: (_path: Array<string | number>) => Array<Array<string | number>> = (
		_path,
	) => {
		// 子の path は不要なので削除
		_path.pop();
		if (_path.length === 0) return [];
		return _path.map((_item, _index) => _path.slice(0, _index + 1));
	};
}
